import { css, CSSObject, SerializedStyles } from '@emotion/react';

import {
    FieldSize,
    FieldTextAlign,
    createResponsiveStyles,
    useHoneycombTheme,
    ResponsiveStyleValue,
    FieldCornerStyle,
    FieldDirection,
} from '../../theming';

export const STATIC_STYLES: SerializedStyles = css`
    --field-value-font-weight: var(--hc-body-font-weight-bold);
    --field-placeholder-font-weight: var(--hc-body-font-weight-semi-bold);
    --field-placeholder-opacity: 0.7;
    --field-label-font-weight: var(--hc-body-font-weight-semi-bold);
    --field-label-font-weight-shrunk: var(--hc-body-font-weight-regular);

    --field-color-outlined-bg: var(--hc-color-common-white);
    --field-color-filled-bg: var(--hc-color-neutral-tint-100);

    --field-border-width: 1px;
    --field-border-width-focused: 2px;

    --field-text-align: start;
    --field-shrink-label-scale: 0.75;

    --field-padding-x: calc(var(--hc-spacing-unit) * 3);
    --field-element-spacing: calc(var(--hc-spacing-unit) * 2);
    --field-icon-size: 20px;
    --field-select-indicator-size: var(--icon-size-2xs);

    --field-height-default: 54px;
    --field-radius-default: 8px;
    --field-label-center-offset-default: calc(var(--hc-spacing-unit) * 2);

    --field-height: var(--field-height-default);
    --field-radius: var(--field-radius-default);

    --field-text-height: calc(var(--hc-base-font-size) * var(--hc-base-line-height)); // 16px * line height (1.5)

    --field-root-padding-start: 0px;
    --field-root-padding-end: 0px;

    // By default we apply horizontal padding to the control
    --field-control-padding-start: var(--field-padding-x);
    --field-control-padding-end: var(--field-padding-x);

    :where([data-with-start-decorator], [data-with-chips]) {
        // Move start padding to the root
        --field-root-padding-start: var(--field-padding-x);
        --field-control-padding-start: 0px;
    }

    :where([data-with-end-decorator]) {
        // Move end padding to the root
        --field-root-padding-end: var(--field-padding-x);
        --field-control-padding-end: 0px;
    }

    :where([data-contains='select']) {
        // Increase end padding to accomodate the select indicator
        --field-control-padding-end: calc(
            var(--field-padding-x) + var(--field-select-indicator-size) + var(--field-element-spacing)
        );
    }

    &:not([data-with-label]) {
        --field-label-center-offset: 0px;
    }

    --field-control-padding-top: calc(
        (var(--field-height) - var(--field-text-height)) / 2 + var(--field-label-center-offset)
    );
    --field-control-padding-bottom: calc(
        var(--field-height) - var(--field-text-height) - var(--field-control-padding-top)
    );

    // root element
    position: relative;
    color: var(--hc-color-text-field);
    display: inline-flex;
    align-items: center;
    gap: var(--field-element-spacing);
    width: auto;
    background-color: var(--field-color-outlined-bg);
    text-align: var(--field-text-align);
    direction: var(--field-direction, inherit);
    border-radius: var(--field-radius);
    padding-inline-start: var(--field-root-padding-start);
    padding-inline-end: var(--field-root-padding-end);
    overflow: hidden;

    // We use a pseudo element for the border
    &::before {
        content: '';
        pointer-events: none; // Let clicks pass through to the control
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        width: 100%;
        height: 100%;
        border: solid var(--field-border-width) var(--hc-color-border);
        border-radius: inherit;
        z-index: 1;
    }

    &[data-variant='filled'] {
        background-color: var(--field-color-filled-bg);
        &::before {
            border-color: transparent;
        }
    }

    &[data-variant='subtle'] {
        background-color: transparent;
        &::before {
            border-color: transparent;
        }
    }

    &[data-full-width] {
        display: flex;
        width: 100%;
    }

    &[data-disabled] {
        color: var(--hc-color-disabled);

        &::before {
            border-color: var(--hc-color-disabled);
        }
    }

    &[data-focused]::before,
    &[data-status='highlight']::before {
        border-color: var(--hc-color-system-info-base);
    }

    &[data-status='error']::before {
        border-color: var(--hc-color-system-error-base);
    }

    &[data-status='success']::before {
        border-color: var(--hc-color-system-success-base);
    }

    &[data-focused]::before {
        border-width: var(--field-border-width-focused);
    }

    > [data-inner] {
        position: relative;
        display: flex;
        flex-direction: row-reverse; // Puts the control first in the tab sequence
        gap: var(--field-element-spacing);
        flex-grow: 1;
        overflow: hidden;
    }

    [data-chips] {
        pointer-events: none; // Clicking between chips should focus the input
        [data-chip] {
            pointer-events: auto;
        }

        display: flex;
        align-self: flex-end;
        justify-content: flex-end;
        overflow: hidden;
        white-space: nowrap;
        flex: 0 1 auto;
        max-width: 100%;
        gap: var(--hc-spacing-unit);
        padding-bottom: var(--field-control-padding-bottom);
    }

    [data-control] {
        font-size: 1rem;
        min-height: var(--field-height);
        background-color: transparent;

        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        border: none;
        line-height: inherit;
        text-align: inherit;
        font-weight: var(--field-value-font-weight);

        padding-top: var(--field-control-padding-top);
        padding-bottom: var(--field-control-padding-bottom);

        padding-inline-start: var(--field-control-padding-start);
        padding-inline-end: var(--field-control-padding-end);

        &::placeholder {
            font-weight: var(--field-placeholder-font-weight);
            opacity: var(--field-placeholder-opacity);
        }

        &:disabled: {
            opacity: 1;
            pointer-events: none;
        }

        // The input should have a min width of 25% when the field contains chips
        flex: 1 1 25%;
        min-width: 25%;
    }

    [data-iframe] {
        height: var(--field-height);
    }

    [data-label] {
        z-index: 1;
        position: absolute;
        inset-inline-start: var(--field-control-padding-start);
        top: calc((var(--field-height) - var(--field-text-height)) / 2);
        color: currentColor;
        font-weight: var(--field-label-font-weight);
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        pointer-events: none;
        max-width: calc(100% - var(--field-control-padding-start) - var(--field-control-padding-end));

        &[data-shrink] {
            user-select: none;
            pointer-events: auto;
            font-weight: var(--field-label-font-weight-shrunk);
            max-width: calc(130% - var(--field-control-padding-start) - var(--field-control-padding-end));
            transform: translateY(calc(var(--field-label-center-offset) * -1)) scale(var(--field-shrink-label-scale));
        }

        transform-origin: top left;
        &:dir(rtl) {
            transform-origin: top right;
        }
        transition: transform var(--hc-transition-duration-shorter) ease-out,
            max-width var(--hc-transition-duration-shorter) ease-out;
    }

    // We don't want to animate the label for buttons
    &[data-contains='button'] [data-label] {
        transition: none;
    }

    > [data-start-decorator],
    > [data-end-decorator] {
        flex-shrink: 0;
    }

    > [data-start-decorator] [data-icon] {
        pointer-events: none;
        color: currentColor;

        width: var(--field-icon-size);
        height: var(--field-icon-size);
    }

    [data-select-indicator] {
        pointer-events: none;
        position: absolute;
        top: 50%;
        inset-inline-end: var(--field-padding-x);
        transform: translateY(-50%);
        color: currentColor;
        width: var(--field-select-indicator-size);
        height: var(--field-select-indicator-size);
    }

    > [data-button] {
        &:disabled: {
            opacity: 1;
            color: currentColor;
        }
    }
`;

export interface FieldBaseDynamicStylesParams {
    textAlign: FieldTextAlign;
    size: ResponsiveStyleValue<FieldSize>;
    cornerStyle?: FieldCornerStyle;
    direction?: FieldDirection;
}

export function useDynamicStyles({ textAlign, size, cornerStyle, direction }: FieldBaseDynamicStylesParams): CSSObject {
    const { theme } = useHoneycombTheme();
    const {
        components: {
            themes: { FieldBase: componentTheme },
        },
        spacingUnit,
    } = theme;

    return {
        '--field-text-align': textAlign,
        '--field-direction': direction,
        // These styles are size dependent
        ...createResponsiveStyles(
            [
                {
                    value: size,
                    getStyles: (value: FieldSize) => {
                        const sizeProps = componentTheme.size[value];
                        return {
                            '--field-height': `${sizeProps.height}px`,
                            '--field-radius': cornerStyle === 'rounded' ? `${sizeProps.radius}px` : '0px',
                            '--field-padding-x': `${sizeProps.paddingX * spacingUnit}px`,
                            '--field-label-center-offset': `${sizeProps.labelCenterOffset * spacingUnit}px`,
                        };
                    },
                },
            ],
            theme
        ),
    };
}
